import { useState, useRef, useEffect } from "react";
import BaseModal from "@components/modals/BaseModal";
import Icon1 from "@assets/icons/Icon";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import DatePicker from "@components/ui/selection/DatePicker";
import TimePicker from "@components/ui/selection/TimePicker";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import { dossiersApi } from "@services/api/dossiers";
import { ordresApi } from "@services/api/ordres";
import { vehiclesApi } from "@services/api/vehicles";
import { VehicleThumb } from "@components/ui/RentalVehicleBadge";
import { useOrdreStore } from "@store/ordreStore";
import { showNotification } from "context/notification.service";
import { TrackingContentV2 } from "@screens/TrackingScreenV2";
import { getTrackingData, type TrackingData } from "@services/api/tracking";
import type { Ordre } from "types/ordre";
import type { Dossier } from "types/dossier";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";
import { hourToTimeStr, timeStrToHour } from "@utils/timeGrid";

/**
 * Sub-modals opened from the ordre "Actions" menu. Extracted from
 * OrdreDetailScreen so both the Ordre page and the OrdreModal (opened from the
 * planning) can render them — see OrdreActionsMenu.
 */

const DOCUMENT_TYPE_OPTIONS = [
  { value: "carte_grise", label: "Carte grise" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "constat_amiable", label: "Constat amiable" },
  { value: "permis_conduire", label: "Permis de conduire" },
  { value: "devis", label: "Devis" },
  { value: "facture", label: "Facture" },
  { value: "bon_commande", label: "Bon de commande" },
  { value: "bon_livraison", label: "Bon de livraison" },
  { value: "contrat_location", label: "Contrat de location / prêt" },
  { value: "rapport_expertise", label: "Rapport d'expertise" },
  { value: "photo_vehicule", label: "Photo véhicule" },
  { value: "photo_degats", label: "Photo dégâts" },
  { value: "courrier", label: "Courrier" },
  { value: "autre", label: "Autre" },
];

const MOBILITY_LABELS: Record<string, string> = {
  attente: "Attente sur place",
  velo: "Vélo",
  pret: "Véhicule de prêt",
  location: "Véhicule de location",
};

const USAGE_FOR_MOBILITY: Record<string, string> = {
  velo: "velos",
  pret: "pret",
  location: "location",
};

// ─── Add document modal — uploads to parent dossier ──────────

export function AddDocumentModalOrdre({
  dossierId,
  onClose,
  onSuccess,
}: {
  dossierId: string;
  onClose: () => void;
  onSuccess: (dossier: Dossier) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);
    setClassifying(true);
    try {
      const result = await dossiersApi.classifyDocument(selectedFile);
      if (result.suggested_name) {
        const origExt = selectedFile.name.includes(".") ? selectedFile.name.substring(selectedFile.name.lastIndexOf(".")) : "";
        const suggestedHasExt = result.suggested_name.includes(".");
        setDocName(suggestedHasExt ? result.suggested_name : result.suggested_name + origExt);
      }
      if (result.type_key) setDocType(result.type_key);
    } catch { /* user can manually select */ }
    finally { setClassifying(false); }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      let uploadFile = file;
      if (file.type.startsWith("image/")) {
        const { compressImage } = await import("@utils/compressImage");
        uploadFile = await compressImage(file);
      }
      const updated = await dossiersApi.uploadDocument(dossierId, uploadFile, docName || undefined, docType || undefined);
      showNotification({ type: "success", message: "Document ajouté" });
      onSuccess(updated);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally { setUploading(false); }
  };

  return (
    <BaseModal title="Ajouter un document" startIcon={<Icon1 name="upload" size={20} />} onClose={onClose} width="max-w-md">
      <div className="flex flex-col gap-4 p-4">
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f); }}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF, images, documents</span>
            </div>
          )}
          <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }} />
        </div>
        {classifying && (
          <div className="flex flex-row items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
            <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-h3 text-indigo-600">Analyse du document...</span>
          </div>
        )}
        <TextField label="Nom du document" value={docName} onChange={setDocName} />
        <DropDown label="Type de document" options={DOCUMENT_TYPE_OPTIONS} value={docType} onSelect={(v) => setDocType(String(v))} />
      </div>
      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label={uploading ? "Envoi..." : "Ajouter"} onClick={handleUpload} disabled={!file || uploading} />
      </div>
    </BaseModal>
  );
}

// ─── Facture upload modal — uploads to ordre documents ───────

export function FactureUploadModal({
  ordreId,
  onClose,
  onSuccess,
}: {
  ordreId: string;
  onClose: () => void;
  onSuccess: (ordre: Ordre) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [docName, setDocName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    setDocName(selectedFile.name);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const updated = await ordresApi.uploadDocument(ordreId, file, docName || "Facture", "facture");
      showNotification({ type: "success", message: "Facture ajoutée" });
      onSuccess(updated);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de l'envoi" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <BaseModal title="Ajouter la facture" startIcon={<Icon1 name="file" size={20} />} onClose={onClose} width="max-w-md">
      <div className="flex flex-col gap-4 p-4">
        <div
          className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl p-6 cursor-pointer transition-colors ${dragOver ? "border-indigo-400 bg-indigo-50" : "border-neutral-300 hover:bg-neutral-50"}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFileSelect(f); }}
        >
          {file ? (
            <div className="flex flex-col items-center gap-2">
              <Icon1 name="file" size={28} color="#6366f1" />
              <span className="text-h3 font-medium text-neutral-700 text-center">{file.name}</span>
              <span className="text-h4 text-neutral-400">{(file.size / 1024).toFixed(0)} Ko</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Icon1 name="upload" size={24} color="#A1A1A1" />
              <span className="text-h3 text-neutral-400">Glisser ou cliquer pour importer</span>
              <span className="text-h4 text-neutral-300">PDF ou image de la facture</span>
            </div>
          )}
          <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }} />
        </div>
        <TextField label="Nom du document" value={docName} onChange={setDocName} />
      </div>
      <div className="border-t p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label={uploading ? "Envoi..." : "Ajouter la facture"} onClick={handleUpload} disabled={!file || uploading} />
      </div>
    </BaseModal>
  );
}

// ─── Rental vehicle assignment modal ────────────────────────

/** ISO (YYYY-MM-DD) ↔ secondes unix, au midi local pour échapper aux bascules de fuseau. */
function isoToSeconds(iso: string): number | null {
  if (!iso) return null;
  return Math.floor(new Date(`${iso}T12:00:00`).getTime() / 1000);
}

function secondsToIso(seconds: number): string {
  const d = new Date(seconds * 1000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function RentalVehicleModal({
  ordre,
  orgId,
  existingReservations = [],
  onClose,
  onAssign,
}: {
  ordre: Ordre;
  orgId: string;
  /** Mobilités déjà posées sur l'ordre : la période proposée démarre APRÈS la dernière. */
  existingReservations?: VehicleReservation[];
  onClose: () => void;
  onAssign: (vehicle: Vehicle, reservationId: string) => void;
}) {
  const [available, setAvailable] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState<string | null>(null);
  const matchingUsage = USAGE_FOR_MOBILITY[ordre.mobility_option || ""] || null;
  // Category filter defaults to the client's requested usage (if any), plus a
  // free-text search over name / plate / make / model.
  const [categoryFilter, setCategoryFilter] = useState<"all" | "pret" | "location" | "velos">(
    (matchingUsage as "pret" | "location" | "velos") || "all",
  );
  const [search, setSearch] = useState("");

  const todayStr = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const weekLaterStr = (() => { const d = new Date(); d.setDate(d.getDate() + 7); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();

  const ordreStartDate = ordre.reception_date
    ? (() => { const d = new Date(ordre.reception_date.seconds * 1000); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })()
    : todayStr;
  const ordreEndDate = ordre.restitution
    ? (() => { const d = new Date(ordre.restitution.seconds * 1000); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })()
    : ordreStartDate || weekLaterStr;

  const ordreStartHour = ordre.reception_date
    ? new Date(ordre.reception_date.seconds * 1000).getHours() + new Date(ordre.reception_date.seconds * 1000).getMinutes() / 60
    : 8;
  const ordreReturnHour = ordre.restitution
    ? new Date(ordre.restitution.seconds * 1000).getHours() + new Date(ordre.restitution.seconds * 1000).getMinutes() / 60
    : 17;

  // Un ordre peut enchaîner plusieurs mobilités : un prêt du 1 au 8, puis une location du
  // 8 au 15. La période proposée reprend donc là où la dernière s'arrête — deux mobilités
  // ont le droit de SE SUIVRE, jamais de se chevaucher (le back refuse en 409).
  const lastEnd = (r: VehicleReservation) =>
    r.returnHour
    ?? (r.startHour != null && r.durationHours != null ? r.startHour + r.durationHours : 17);
  const lastReservation = [...existingReservations]
    .sort((a, b) => (a.endDate || "").localeCompare(b.endDate || "") || lastEnd(a) - lastEnd(b))
    .pop();
  const initialStart = lastReservation?.endDate || ordreStartDate;
  const initial = lastReservation
    ? {
        start: initialStart,
        end: initialStart > ordreEndDate ? initialStart : ordreEndDate,
        sh: lastEnd(lastReservation),
        rh: ordreReturnHour,
      }
    : { start: ordreStartDate, end: ordreEndDate, sh: ordreStartHour, rh: ordreReturnHour };

  const [startDate, setStartDate] = useState(initial.start);
  const [endDate, setEndDate] = useState(initial.end);
  const [startHour, setStartHour] = useState(initial.sh);
  const [returnHour, setReturnHour] = useState(initial.rh);

  useEffect(() => {
    setLoading(true);
    vehiclesApi.available({
      org_id: orgId,
      start_date: startDate,
      end_date: endDate,
      start_hour: startHour,
      end_hour: returnHour,
      // Manual attribution on an existing ordre: the booking-side category filter
      // doesn't apply, staff can still hand out any class.
      all_categories: true,
    })
      .then((v) => { setAvailable(v); })
      .catch(() => { setAvailable([]); })
      .finally(() => setLoading(false));
  }, [orgId, startDate, endDate, startHour, returnHour]);

  const handleAssign = async (vehicle: Vehicle) => {
    if (!startDate || !endDate) return;
    setAssigning(vehicle.id);
    try {
      const clientName = ordre.name || ordre.contacts?.[0]?.nom || "Client";
      const reservation = await vehiclesApi.createReservation({
        org_id: orgId,
        vehicleId: vehicle.id,
        clientName,
        startDate,
        endDate,
        hasPickup: true,
        hasReturn: true,
        startHour,
        returnHour: startDate === endDate ? undefined : returnHour,
        durationHours: startDate === endDate ? Math.max(0.25, returnHour - startHour) : undefined,
        ordreId: ordre.id,
      });

      // Le back recale lui-même rental_vehicle_id / rental_reservation_id sur la mobilité
      // COURANTE de l'ordre : les écrire ici épinglerait celle qu'on vient d'ajouter, même
      // quand elle ne commence que la semaine prochaine.
      const updatedOrdre = await ordresApi.get(ordre.id);

      ordresApi.addVehicleBookingEvent(ordre.id, {
        vehicle_plate: vehicle.plate,
        vehicle_name: vehicle.name,
        start_date: startDate,
        end_date: endDate,
      }).catch((e) => console.error("Error adding booking timeline event:", e));

      useOrdreStore.getState().updateOrdre(updatedOrdre);
      onAssign(vehicle, reservation.id);
      showNotification({ type: "success", message: `${vehicle.name} (${vehicle.plate}) attribué` });
      onClose();
    } catch (e: any) {
      showNotification({
        type: "error",
        message: e?.response?.status === 409
          ? e.response.data?.detail || "Une autre mobilité occupe déjà cette période."
          : "Erreur lors de l'attribution",
      });
    } finally {
      setAssigning(null);
    }
  };

  const usageLabel = MOBILITY_LABELS[ordre.mobility_option || ""] || "Véhicule de remplacement";
  const vehicleCategory = (v: Vehicle) =>
    v.usage === "location" ? "location" : v.usage === "velos" ? "velos" : "pret";
  const searchQ = search.trim().toLowerCase();
  const filteredVehicles = available.filter((v) => {
    if (categoryFilter !== "all" && vehicleCategory(v) !== categoryFilter) return false;
    if (searchQ) {
      const hay = `${v.name} ${v.plate} ${v.marque || ""} ${v.modele || ""}`.toLowerCase();
      if (!hay.includes(searchQ)) return false;
    }
    return true;
  });
  const CATEGORY_TABS: { key: "all" | "pret" | "location" | "velos"; label: string }[] = [
    { key: "all", label: "Tous" },
    { key: "pret", label: "Prêt" },
    { key: "location", label: "Location" },
    { key: "velos", label: "Vélo" },
  ];

  return (
    <BaseModal
      title={existingReservations.length ? "Ajouter une mobilité" : "Attribuer un véhicule"}
      startIcon={<Icon1 name="side-ordres" size={20} />}
      onClose={onClose}
      width="max-w-lg"
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-1 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
          <div className="flex flex-row items-center gap-2">
            <span className="text-h3 text-neutral-400">Demande client :</span>
            <span className="text-h3 font-medium text-black">{usageLabel}</span>
          </div>
        </div>

        {/* Période modifiable : une 2ᵉ mobilité (la location qui prend la suite du prêt)
            ne couvre pas la même période que la réparation. La liste des véhicules libres
            suit ce que l'on saisit ici. */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <DatePicker
                label="Début"
                seconds={isoToSeconds(startDate)}
                onChange={(sec) => {
                  const iso = secondsToIso(sec);
                  setStartDate(iso);
                  if (iso > endDate) setEndDate(iso);
                }}
              />
            </div>
            <div className="flex-1">
              <DatePicker
                label="Fin"
                seconds={isoToSeconds(endDate)}
                minDateSeconds={isoToSeconds(startDate)}
                onChange={(sec) => setEndDate(secondsToIso(sec))}
              />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex-1">
              <TimePicker
                label="Heure de départ"
                value={hourToTimeStr(startHour)}
                onChange={(t) => { if (t) setStartHour(timeStrToHour(t)); }}
              />
            </div>
            <div className="flex-1">
              <TimePicker
                label="Heure de retour"
                value={hourToTimeStr(returnHour)}
                onChange={(t) => { if (t) setReturnHour(timeStrToHour(t)); }}
              />
            </div>
          </div>
          {lastReservation && (
            <span className="text-h4 text-neutral-400">
              Mobilité précédente jusqu'au {lastReservation.endDate} — la nouvelle prend la
              suite. Deux mobilités ne peuvent pas se chevaucher.
            </span>
          )}
        </div>

        {/* Category tabs + search */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-1">
            {CATEGORY_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setCategoryFilter(t.key)}
                className={`px-3 py-1.5 text-h4 font-medium rounded-lg transition-colors ${
                  categoryFilter === t.key
                    ? "bg-[#5C6BFF] text-white"
                    : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <TextField
            value={search}
            onChange={setSearch}
            placeholder="Rechercher (nom, plaque, modèle)…"
          />
        </div>

        {!startDate || !endDate ? (
          <div className="text-h3 text-center text-neutral-400 py-6">
            Définissez les dates de réception et restitution pour voir les véhicules disponibles.
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-6">
            <span className="text-h3 text-neutral-400">
              {available.length > 0 && (categoryFilter !== "all" || searchQ)
                ? "Aucun véhicule ne correspond à ce filtre pour cette période."
                : "Aucun véhicule disponible pour cette période."}
            </span>
            {available.length > 0 && (categoryFilter !== "all" || searchQ) && (
              <button
                className="text-h3 font-medium text-[#5C6BFF] hover:underline"
                onClick={() => { setCategoryFilter("all"); setSearch(""); }}
              >
                Réinitialiser les filtres ({available.length})
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1 max-h-[400px] overflow-y-auto">
            {filteredVehicles.map((v) => (
              <button
                key={v.id}
                disabled={!!assigning}
                onClick={() => handleAssign(v)}
                className="flex flex-row items-center gap-3 p-3 rounded-lg border border-neutral-200 hover:border-[#5C6BFF] hover:bg-[#F5F6FF] transition-colors text-left"
              >
                {/* La photo : choisir un véhicule de prêt se fait à l'œil, pas
                    sur une icône générique identique pour toute la flotte. */}
                <div className="w-16 h-12 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <VehicleThumb vehicle={v} width={60} height={44} />
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-h2 font-medium text-black truncate">{v.name}</span>
                  <span className="text-h4 text-neutral-400">
                    {v.plate} · {v.marque || ""} {v.modele || ""} · {v.km} km
                  </span>
                </div>
                <span
                  className="text-h4 font-medium rounded-full px-2 py-0.5"
                  style={{
                    background: v.usage === "location" ? "#FEF3C7" : v.usage === "velos" ? "#DBEAFE" : "#D1FAE5",
                    color: v.usage === "location" ? "#92400E" : v.usage === "velos" ? "#1E40AF" : "#065F46",
                  }}
                >
                  {v.usage === "location" ? "Location" : v.usage === "velos" ? "Vélo" : "Prêt"}
                </span>
                {assigning === v.id && (
                  <div className="w-4 h-4 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end">
        <SecondaryButton variant="small" onClick={onClose}>Fermer</SecondaryButton>
      </div>
    </BaseModal>
  );
}

// ─── Tracking preview modal (phone frame) ─────────────────

export function TrackingPreviewModal({
  ordre,
  onClose,
}: {
  ordre: Ordre;
  parentDossier?: Dossier | null;
  orgData?: any;
  onClose: () => void;
}) {
  const [hoveredStar, setHoveredStar] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getTrackingData(ordre.id)
      .then((d) => { setTrackingData(d); setSelectedRating(d.client_rating || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ordre.id, ordre.status, ordre.tracking_step, ordre.acompte_paid, ordre.solde_paid, ordre.devis_signed]);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col border rounded-[15px] overflow-hidden shadow-xl" style={{ width: 380, maxHeight: "calc(100vh - 48px)", background: "#FFFFFF", borderColor: "#D9D9D9" }}>
      <div className="flex flex-row items-center justify-between px-[20px] py-[12px] border-b border-[#E5E5E5]">
        <div className="flex flex-col">
          <span className="text-h3 text-neutral-400">Aperçu client</span>
          <span className="text-h2 font-medium" style={{ color: "#5C6BFF" }}>
            Suivi #{ordre.or_number || ordre.num}
          </span>
        </div>
        <button onClick={onClose} className="hover:bg-neutral-100 rounded-full p-1">
          <svg width={12} height={12} viewBox="0 0 12 12" fill="none">
            <path d="M1 1L11 11M11 1L1 11" stroke="#A1A1A1" strokeWidth={1.5} strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-[#5C6BFF] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : trackingData ? (
          <TrackingContentV2
            data={trackingData}
            ordreId={ordre.id}
            hoveredStar={hoveredStar}
            setHoveredStar={setHoveredStar}
            selectedRating={selectedRating}
            setSelectedRating={setSelectedRating}
            previewMode
            onDataRefresh={() => {
              getTrackingData(ordre.id)
                .then((d) => { setTrackingData(d); setSelectedRating(d.client_rating || 0); })
                .catch(() => {});
            }}
          />
        ) : (
          <div className="flex items-center justify-center py-12">
            <span className="text-sm text-gray-400">Impossible de charger l'aperçu</span>
          </div>
        )}
      </div>
    </div>
  );
}
